/**
 * 🛒 SHARED CART STORE (Composable)
 *
 * Pinia-based cart management for all restaurant projects
 * Compatible with server OrderItemRequest format
 */
import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { generateCartConfigId, cartItemToOrderRequest, validateCart } from '@shared/types/cart';
const CART_STORAGE_KEY = 'restaurant-cart';
const CART_EXPIRY_HOURS = 48; // Cart expires after 48 hours
export const useCartStore = defineStore('cart', () => {
    // ================================
    // STATE
    // ================================
    const items = ref([]);
    const isOpen = ref(false);
    const deliveryType = ref('pickup');
    const selectedTime = ref('');
    const orderNotes = ref('');
    const customerData = ref({
        name: '',
        phone: '',
        email: ''
    });
    const deliveryAddress = ref({
        street: '',
        city: '',
        postalCode: ''
    });
    // ================================
    // PERSISTENCE HELPERS
    // ================================
    /**
     * Load cart from localStorage
     */
    const loadCartFromStorage = () => {
        try {
            if (typeof Storage === 'undefined') {
                console.warn('⚠️ localStorage not available');
                return;
            }
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (stored) {
                const cartData = JSON.parse(stored);
                // Check if cart has expired
                const now = Date.now();
                const hoursElapsed = (now - cartData.timestamp) / (1000 * 60 * 60);
                if (hoursElapsed < CART_EXPIRY_HOURS) {
                    // Migrate and validate items (defensive against corrupted legacy payloads)
                    const migratedItems = (Array.isArray(cartData.items) ? cartData.items : [])
                        .map((rawItem) => {
                        try {
                            return migrateCartItem(rawItem);
                        }
                        catch (migrateError) {
                            console.warn('⚠️ Skipping invalid cart item during migration:', migrateError);
                            return null;
                        }
                    })
                        .filter(item => item !== null);
                    // Regenerate configId for all loaded items (defensive for malformed config structures)
                    items.value = migratedItems
                        .map((item) => {
                        try {
                            return {
                                ...item,
                                configId: generateCartConfigId(item)
                            };
                        }
                        catch (configIdError) {
                            console.warn('⚠️ Skipping invalid cart item during configId generation:', configIdError);
                            return null;
                        }
                    })
                        .filter((item) => item !== null);
                    deliveryType.value = cartData.deliveryType || 'pickup';
                    selectedTime.value = cartData.selectedTime || '';
                    orderNotes.value = cartData.orderNotes || '';
                    if (cartData.customerData) {
                        customerData.value = { ...cartData.customerData };
                    }
                    if (cartData.deliveryAddress) {
                        deliveryAddress.value = { ...cartData.deliveryAddress };
                    }
                    console.log(`✅ Cart loaded: ${items.value.length} items, ${Math.round(hoursElapsed)}h old`);
                    // Recalculate prices to ensure consistency
                    recalculateAllPrices();
                }
                else {
                    console.log(`🗑️ Cart expired (${Math.round(hoursElapsed)}h), clearing`);
                    localStorage.removeItem(CART_STORAGE_KEY);
                }
            }
        }
        catch (error) {
            console.error('❌ Error loading cart:', error);
            try {
                localStorage.removeItem(CART_STORAGE_KEY);
            }
            catch (cleanupError) {
                console.error('❌ Error cleaning up:', cleanupError);
            }
        }
    };
    /**
     * Migrate legacy cart item to ensure priceCent fields
     */
    const migrateCartItem = (item) => {
        if (!item || typeof item !== 'object') {
            return null;
        }
        if (!item.menuItem || typeof item.menuItem !== 'object') {
            console.warn('⚠️ Removing item without menuItem');
            return null;
        }
        // Ensure priceCent exists
        if (item.menuItem && !item.menuItem.priceCent && item.menuItem.price) {
            item.menuItem.priceCent = Math.round(item.menuItem.price * 100);
        }
        // Ensure extras have priceCent
        if (Array.isArray(item.extras)) {
            item.extras = item.extras.map(extra => ({
                ...extra,
                priceCent: extra.priceCent || Math.round(extra.price * 100)
            }));
        }
        else {
            item.extras = [];
        }
        // Ensure selectedSize has priceCent
        if (item.selectedSize && !item.selectedSize.priceCent && item.selectedSize.price) {
            item.selectedSize.priceCent = Math.round(item.selectedSize.price * 100);
        }
        // Remove items without valid price
        if (!item.menuItem.priceCent && !item.menuItem.price) {
            console.warn('⚠️ Removing item without price:', item.menuItem.name);
            return null;
        }
        return item;
    };
    /**
     * Save cart to localStorage
     */
    const saveCartToStorage = () => {
        try {
            const cartData = {
                items: items.value,
                timestamp: Date.now(),
                deliveryType: deliveryType.value,
                selectedTime: selectedTime.value,
                orderNotes: orderNotes.value,
                customerData: customerData.value,
                deliveryAddress: deliveryAddress.value
            };
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
        }
        catch (error) {
            console.error('❌ Error saving cart:', error);
        }
    };
    // ================================
    // INITIALIZATION
    // ================================
    let isLoading = false;
    isLoading = true;
    loadCartFromStorage();
    isLoading = false;
    // Auto-save on changes
    watch([() => items.value, deliveryType, selectedTime, orderNotes, customerData, deliveryAddress], () => {
        if (!isLoading) {
            saveCartToStorage();
        }
    }, { deep: true });
    // ================================
    // COMPUTED PROPERTIES
    // ================================
    const itemCount = computed(() => {
        return items.value.reduce((total, item) => total + item.quantity, 0);
    });
    const totalPrice = computed(() => {
        return items.value.reduce((total, item) => total + item.totalPrice, 0);
    });
    const totalPriceCent = computed(() => {
        return Math.round(totalPrice.value * 100);
    });
    const isEmpty = computed(() => items.value.length === 0);
    const validation = computed(() => validateCart(items.value));
    // ================================
    // PRICE CALCULATION
    // ================================
    /**
     * Calculate total price for a cart item configuration
     */
    const calculateItemPrice = (config) => {
        // Use server-calculated price if available
        if (config.serverCalculatedPriceCent && config.quantity) {
            return config.serverCalculatedPriceCent / 100;
        }
        // Calculate client-side
        const basePrice = config.menuItem.priceCent
            ? config.menuItem.priceCent / 100
            : (config.menuItem.price || 0);
        // Size modifier (added to base price)
        let unitPrice;
        if (config.selectedSize) {
            if ('priceCent' in config.selectedSize && config.selectedSize.priceCent !== undefined) {
                const sizeModifier = config.selectedSize.priceCent / 100;
                unitPrice = basePrice + sizeModifier;
            }
            else if ('price' in config.selectedSize) {
                unitPrice = basePrice + config.selectedSize.price;
            }
            else {
                unitPrice = basePrice;
            }
        }
        else {
            unitPrice = basePrice;
        }
        // Add extras
        const extrasPrice = (config.extras || []).reduce((sum, extra) => {
            return sum + (extra.priceCent ? extra.priceCent / 100 : extra.price || 0);
        }, 0);
        const itemPriceWithExtras = unitPrice + extrasPrice;
        return itemPriceWithExtras * config.quantity;
    };
    /**
     * Recalculate prices for all cart items
     */
    function recalculateAllPrices() {
        items.value.forEach(item => {
            item.totalPrice = calculateItemPrice({
                menuItem: item.menuItem,
                selectedSize: item.selectedSize,
                extras: item.extras,
                quantity: item.quantity
            });
        });
        console.log('🔄 Recalculated all cart prices');
    }
    // ================================
    // CART ACTIONS
    // ================================
    /**
     * Add item to cart or increase quantity if same configuration exists
     */
    const addItem = (product) => {
        const configId = generateCartConfigId(product);
        // Find existing item with same configuration
        const existingItem = items.value.find(item => item.id === product.id && item.configId === configId);
        if (existingItem) {
            // Increase quantity of existing item
            existingItem.quantity += (product.quantity || 1);
            existingItem.totalPrice = calculateItemPrice({
                menuItem: existingItem.menuItem,
                selectedSize: existingItem.selectedSize,
                extras: existingItem.extras,
                quantity: existingItem.quantity,
                serverCalculatedPriceCent: product.calculatedPriceCent
            });
        }
        else {
            // Create new cart item
            const menuItem = {
                id: product._id || product.id,
                _id: product._id,
                name: product.name,
                description: product.description,
                price: product.price,
                priceCent: product.priceCent,
                originalPrice: product.originalPrice,
                originalPriceCent: product.originalPriceCent,
                category: product.category || 'general',
                imageUrl: product.image,
                allergens: product.allergens,
                isVegetarian: product.isVegetarian,
                isVegan: product.isVegan,
                isGlutenFree: product.isGlutenFree,
                preparationTime: product.preparationTime,
                isAvailable: product.available !== false,
                popular: product.popular
            };
            const quantity = product.quantity || 1;
            const totalPrice = calculateItemPrice({
                menuItem,
                selectedSize: product.selectedSize,
                extras: product.extras,
                quantity,
                serverCalculatedPriceCent: product.calculatedPriceCent
            });
            const newItem = {
                id: product._id || product.id,
                menuItem,
                quantity,
                totalPrice,
                extras: product.extras,
                removedIngredients: product.removedIngredients,
                specialInstructions: product.specialInstructions,
                selectedSize: product.selectedSize,
                selectedMandatoryIngredients: product.selectedMandatoryIngredients,
                configId,
                deposit: product.deposit || { type: 'none', amountCent: 0 }
            };
            items.value.push(newItem);
            console.log('🛒 Added to cart:', newItem.menuItem.name, 'x', quantity);
        }
    };
    /**
     * Add combo menu to cart (only when menus feature is enabled on client).
     */
    const addMenuItem = (payload) => {
        const menuId = payload.menu._id || payload.menu.id || '';
        const configId = generateCartConfigId({
            itemType: 'menu',
            menuId,
            componentConfigs: payload.componentConfigs
        });
        const existingItem = items.value.find((item) => item.itemType === 'menu' && item.menuId === menuId && item.configId === configId);
        const quantity = payload.quantity || 1;
        if (existingItem) {
            existingItem.quantity += quantity;
            existingItem.totalPrice = (payload.totalPrice / quantity) * existingItem.quantity;
            if (payload.totalPriceCent !== undefined) {
                existingItem.totalPriceCent = Math.round((payload.totalPriceCent / quantity) * existingItem.quantity);
            }
            return;
        }
        const menuItem = {
            id: menuId,
            _id: menuId,
            name: payload.menu.name,
            description: payload.menu.description,
            price: payload.menu.menuPriceCent / 100,
            priceCent: payload.menu.menuPriceCent,
            category: 'menu'
        };
        const newItem = {
            id: menuId,
            menuId,
            itemType: 'menu',
            menuItem,
            quantity,
            totalPrice: payload.totalPrice,
            totalPriceCent: payload.totalPriceCent,
            componentConfigs: payload.componentConfigs,
            configId,
            deposit: payload.totalDepositCent && payload.totalDepositCent > 0
                ? { type: 'einweg', amountCent: payload.totalDepositCent }
                : { type: 'none', amountCent: 0 }
        };
        items.value.push(newItem);
        console.log('🛒 Added menu to cart:', newItem.menuItem.name, 'x', quantity);
    };
    /**
     * Remove item from cart by configId or id
     */
    const removeItem = (identifier) => {
        const index = items.value.findIndex(item => item.configId === identifier || item.id === identifier);
        if (index > -1) {
            const removedItem = items.value[index];
            items.value.splice(index, 1);
            console.log('🗑️ Removed from cart:', removedItem.menuItem.name);
        }
    };
    /**
     * Update quantity of cart item
     */
    const updateQuantity = (identifier, quantity) => {
        const item = items.value.find(item => item.configId === identifier || item.id === identifier);
        if (item) {
            if (quantity <= 0) {
                removeItem(identifier);
            }
            else {
                item.quantity = quantity;
                item.totalPrice = calculateItemPrice({
                    menuItem: item.menuItem,
                    selectedSize: item.selectedSize,
                    extras: item.extras,
                    quantity: item.quantity
                });
                console.log('🔄 Updated quantity:', item.menuItem.name, 'x', quantity);
            }
        }
    };
    /**
     * Clear all items from cart
     */
    const clearCart = () => {
        items.value = [];
        localStorage.removeItem(CART_STORAGE_KEY);
        console.log('🗑️ Cart cleared');
    };
    /**
     * Clear all data including customer information
     */
    const clearAllData = () => {
        items.value = [];
        deliveryType.value = 'pickup';
        selectedTime.value = '';
        orderNotes.value = '';
        customerData.value = { name: '', phone: '', email: '' };
        deliveryAddress.value = { street: '', city: '', postalCode: '' };
        localStorage.removeItem(CART_STORAGE_KEY);
        console.log('🗑️ All cart data cleared');
    };
    /**
     * Clear cart after successful order (keep customer data)
     */
    const clearCartAfterOrder = () => {
        items.value = [];
        selectedTime.value = '';
        orderNotes.value = '';
        localStorage.removeItem(CART_STORAGE_KEY);
        console.log('✅ Cart cleared after order (customer data preserved)');
    };
    /**
     * Manual cleanup for expired carts
     */
    const cleanupExpiredCart = () => {
        try {
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (stored) {
                const cartData = JSON.parse(stored);
                const now = Date.now();
                const hoursElapsed = (now - cartData.timestamp) / (1000 * 60 * 60);
                if (hoursElapsed >= CART_EXPIRY_HOURS) {
                    localStorage.removeItem(CART_STORAGE_KEY);
                    items.value = [];
                    console.log(`🗑️ Expired cart cleaned up (${Math.round(hoursElapsed)}h)`);
                    return true;
                }
            }
        }
        catch (error) {
            console.error('❌ Error during cleanup:', error);
            localStorage.removeItem(CART_STORAGE_KEY);
            items.value = [];
        }
        return false;
    };
    // ================================
    // UI ACTIONS
    // ================================
    const openCart = () => {
        isOpen.value = true;
    };
    const closeCart = () => {
        isOpen.value = false;
    };
    const toggleCart = () => {
        isOpen.value = !isOpen.value;
    };
    // ================================
    // UTILITY FUNCTIONS
    // ================================
    /**
     * Format price with currency
     */
    const formatPrice = (price, priceCent) => {
        const amount = priceCent !== undefined ? priceCent / 100 : price;
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };
    /**
     * Convert cart to order request format
     */
    const getOrderItems = () => {
        return items.value.map(cartItemToOrderRequest);
    };
    /**
     * Debug storage state
     */
    const debugStorage = () => {
        console.log('🔍 Cart Storage Debug:');
        console.log('📋 Current items:', items.value.length);
        console.log('📦 localStorage available:', typeof Storage !== 'undefined');
        try {
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (stored) {
                const cartData = JSON.parse(stored);
                const now = Date.now();
                const hoursElapsed = (now - cartData.timestamp) / (1000 * 60 * 60);
                console.log('💾 Storage data:');
                console.log('  - Items:', cartData.items?.length || 0);
                console.log('  - Age:', `${Math.round(hoursElapsed)}h`);
                console.log('  - Valid:', hoursElapsed < CART_EXPIRY_HOURS);
                console.log('  - Type:', cartData.deliveryType);
            }
        }
        catch (error) {
            console.error('❌ Error reading storage:', error);
        }
        return {
            currentItems: items.value.length,
            storageAvailable: typeof Storage !== 'undefined',
            hasStoredData: !!localStorage.getItem(CART_STORAGE_KEY)
        };
    };
    return {
        // State
        items,
        isOpen,
        deliveryType,
        selectedTime,
        orderNotes,
        customerData,
        deliveryAddress,
        // Computed
        itemCount,
        totalPrice,
        totalPriceCent,
        isEmpty,
        validation,
        // Actions
        addItem,
        addMenuItem,
        removeItem,
        updateQuantity,
        recalculateAllPrices,
        clearCart,
        clearAllData,
        clearCartAfterOrder,
        cleanupExpiredCart,
        openCart,
        closeCart,
        toggleCart,
        // Utilities
        formatPrice,
        getOrderItems,
        debugStorage
    };
});
